import {
  isAssetReadOnly,
  Media,
  PermissionValues,
  useCurrentAccount,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  EntitySearchInput,
  FilterHeaders,
  ImageGalleryImage,
  SlideAction,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { isFromActivitiesOrAssetPage } from '../assetUtils';
import { AddFilesLinksHeaderButton } from './addFilesLinksButton';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';
import { FilesContext, FilesContextType, MediaExtended, ThirdPanelMode } from './filesContext';
import { FilterPanel, FilterPanelHeaderActions } from './filesFilterPanel';
import { GalleryHeaderActions, GalleryPanel } from './filesGalleryPanel';
import { RightColumn } from './filesRightColumn';
import { VideoPanel } from './filesVideoPanel';

export const AssetFiles = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { workspaceAsset, invalidateQuery } = useWorkspaceAsset(assetId ?? '');
  const { account } = useCurrentAccount();
  const canEdit = account?.hasPermission(PermissionValues.asset.update);
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [galleryImages, setGalleryImages] = useState<ImageGalleryImage[]>([]);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [thirdPanelMode, setThirdPanelMode] =
    useState<ThirdPanelMode>('gallery');
  const [allFiles, setAllFiles] = useState<MediaExtended[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >(undefined);
  const [filterPanelRequested, setFilterPanelRequested] = useState(false);
  const [videoMedia, setVideoMedia] = useState<Media | null>(null);
  const [isThirdPanelOpen, setIsThirdPanelOpen] = useState(false);
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);

  if (!assetId) {
    return null;
  }

  const handleSearchClose = () => {
    setSearchKeyword('');
  };

  const handleSearch = async (keyword: string) => {
    setSearchKeyword(keyword);
  };

  const leftColumn = {
    title: 'assets.menu.title',
    mainComponent: <AssetCard assetId={assetId} />,
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: false,
    disableScroll: false,
  };

  const contextValue: FilesContextType = {
    galleryImages,
    setGalleryImages,
    galleryInitialIndex,
    setGalleryInitialIndex,
    thirdPanelMode,
    setThirdPanelMode,
    allFiles,
    selectedCategoryId,
    setSelectedCategoryId,
    filterPanelRequested,
    setFilterPanelRequested,
    videoMedia,
    setVideoMedia,
    isThirdPanelOpen,
    setIsThirdPanelOpen,
    isAddSheetOpen,
    setIsAddSheetOpen,
  };

  const rightColumn = {
    title: 'asset.files.title',
    mainComponent: (
      <FilesContext.Provider value={contextValue}>
        <RightColumn
          assetId={assetId}
          searchKeyword={searchKeyword}
          selectedCategoryId={selectedCategoryId}
          onAllFilesLoaded={setAllFiles}
        />
      </FilesContext.Provider>
    ),
    headerComponent: (
      <FilesContext.Provider value={contextValue}>
        <View style={styles.headerActions}>
          <FilterHeaders
            onSearchClose={handleSearchClose}
            onFilterPress={() => {
              setThirdPanelMode('filter');
              setFilterPanelRequested(true);
            }}
            currentSearchKeyword={searchKeyword}
            searchComponent={
              <EntitySearchInput
                filter={{ keyword: searchKeyword }}
                onSearch={handleSearch}
                placeholder="Search files..."
              />
            }
            Routes={{}}
          />
          {workspaceAsset?.asset && !isReadOnlyMode && (
            <AddFilesLinksHeaderButton
              disabled={!canEdit}
            />
          )}
        </View>
      </FilesContext.Provider>
    ),
    showBackButton: true,
    onBackButtonPress: () => {
      try {
        const cameFromActivityOrAsset = isFromActivitiesOrAssetPage(document);

        if (cameFromActivityOrAsset && history?.state?.idx > 0) {
          navigate(-1);
          return;
        } else {
          navigate(`/assets/${assetId}`);
        }
      } catch (e) {
        navigate(`/assets/${assetId}`);
      }
    },
    backButtonTransition: SlideAction.PREV,
  };

  const onThirdPanelClose = () => setIsThirdPanelOpen(false);

  const getThirdColumn = () => {
    switch (thirdPanelMode) {
      case 'video': {
        const videoName = videoMedia?.name || 'Video';
        return {
          title:
            videoName.length > 30
              ? `${videoName.substring(0, 27)}...`
              : videoName,
          mainComponent: (
            <FilesContext.Provider value={contextValue}>
              <VideoPanel media={videoMedia} />
            </FilesContext.Provider>
          ),
          headerComponent: <GalleryHeaderActions />,
          showBackButton: true,
          disableScroll: true,
          onBackButtonPress: onThirdPanelClose,
        };
      }
      case 'filter':
        return {
          title: `Filter (${allFiles.length})`,
          mainComponent: (
            <FilesContext.Provider value={contextValue}>
              <FilterPanel />
            </FilesContext.Provider>
          ),
          headerComponent: (
            <FilterPanelHeaderActions
              onReset={() => setSelectedCategoryId(undefined)}
            />
          ),
          showBackButton: true,
          disableScroll: true,
          onBackButtonPress: onThirdPanelClose,
        };
      case 'gallery':
      default:
        return {
          title:
            galleryImages[galleryInitialIndex]?.name?.substring(0, 30) ||
            'Image',
          mainComponent: (
            <FilesContext.Provider value={contextValue}>
              <GalleryPanel
                images={galleryImages}
                initialIndex={galleryInitialIndex}
              />
            </FilesContext.Provider>
          ),
          headerComponent: <GalleryHeaderActions />,
          showBackButton: true,
          disableScroll: true,
          onBackButtonPress: onThirdPanelClose,
        };
    }
  };

  const thirdColumn = getThirdColumn();

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
    />
  );
};

const styles = StyleSheet.create({
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
});
